import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import type { UserInfo } from '../../api/api-types';
import type { Loadable } from '../../guards/ngrx-loader.guard';
import { state as DealerState } from '../dealer/dealer.reducer';
import { actions } from './login.actions';

interface LoginState {
    loginStart: Loadable<true>;
    oAuthToken: Loadable<string>;
    userInfo: Loadable<UserInfo>;
    formId: Loadable<string>;
    resendCode: boolean;
}

const initialState: LoginState = {
    loginStart: null,
    oAuthToken: null,
    userInfo: null,
    formId: null,
    resendCode: false,
};

export const state = createFeature({
    name: 'login',
    reducer: createReducer(
        initialState,
        on(
            actions.loginStart,
            (state, action): LoginState => ({
                ...state,
                loginStart: null,
                resendCode: action.resend,
            }),
        ),
        on(
            actions.loginStartSuccess,
            (state): LoginState => ({
                ...state,
                loginStart: true,
            }),
        ),
        on(
            actions.loginStartFailure,
            (state): LoginState => ({
                ...state,
                loginStart: false,
            }),
        ),
        on(
            actions.oAuthToken,
            (state): LoginState => ({
                ...state,
                oAuthToken: null,
            }),
        ),
        on(
            actions.oAuthTokenSuccess,
            (state, { token }): LoginState => ({
                ...state,
                oAuthToken: token,
                resendCode: false,
            }),
        ),
        on(
            actions.oAuthTokenFailure,
            (state): LoginState => ({
                ...state,
                oAuthToken: false,
                resendCode: false,
            }),
        ),
        on(
            actions.loadUserInfo,
            (state): LoginState => ({
                ...state,
                userInfo: null,
            }),
        ),
        on(
            actions.loadUserInfoSuccess,
            (state, { userInfo }): LoginState => ({
                ...state,
                userInfo,
            }),
        ),
        on(
            actions.loadUserInfoFailure,
            (state): LoginState => ({
                ...state,
                userInfo: false,
            }),
        ),
        on(
            actions.getFormId,
            (state): LoginState => ({
                ...state,
                formId: null,
            }),
        ),
        on(
            actions.getFormIdSuccess,
            (state, { formId }): LoginState => ({
                ...state,
                formId,
            }),
        ),
        on(
            actions.getFormIdFailure,
            (state): LoginState => ({
                ...state,
                formId: false,
            }),
        ),
        on(
            actions.logoutSuccess,
            (state): LoginState => ({
                ...state,
                oAuthToken: null,
                userInfo: null,
            }),
        ),
        on(
            actions.getJwtFromPartnerWebSuccess,
            (state, { jwt }): LoginState => ({
                ...state,
                oAuthToken: jwt,
            }),
        ),
    ),
    extraSelectors: (baseSelectors) => {
        const selectUserId = createSelector(baseSelectors.selectUserInfo, (userInfo) =>
            userInfo ? userInfo.userId : '',
        );
        const selectIsLoadingUserInfo = createSelector(baseSelectors.selectUserInfo, (userInfo) => userInfo === null);

        const selectFormIds = createSelector(baseSelectors.selectFormId, selectUserId, (formId, userId) => {
            if (!formId || !userId) {
                return null;
            }
            return {
                formId,
                userId,
            };
        });

        const selectAuthToken = createSelector(baseSelectors.selectOAuthToken, (token) => token || undefined);

        const selectIsAdmin = createSelector(baseSelectors.selectUserInfo, (userInfo) =>
            userInfo ? userInfo.isAdmin : false,
        );

        const selectDealerIdOfUser = createSelector(baseSelectors.selectUserInfo, (userInfo) =>
            userInfo ? userInfo.dealerId : null,
        );

        const selectIsLoggedIn = createSelector(
            selectUserId,
            selectDealerIdOfUser,
            selectIsAdmin,
            DealerState.selectDealerOrVehicleMarketDealer,
            (userId, dealerIdOfUser, isAdmin, dealer) =>
                !!userId && !!dealer && !!dealerIdOfUser && (dealerIdOfUser === dealer.id || isAdmin),
        );

        return {
            selectIsLoadingUserInfo,
            selectFormIds,
            selectAuthToken,
            selectUserId,
            selectIsAdmin,
            selectIsLoggedIn,
        };
    },
});
